import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { PaginatedList } from '../models/paginatedList.model';
import { Financial } from '../models/financial.model';
import { FinancialDashboard } from '../models/financialDashboard.model';
import { FinancialSummary } from '../models/financialSummaryDto.model';
import { RestResponse } from '../models/restResponse.mode';
import { DateRangeEnum } from '../models/enums/dateRange.enum';
import { CreateFinancialDto } from '../models/dtos/createFinancial.dto';
import { CreateExpenseDto } from '../models/dtos/createExpense.dto';
import { CreateFinancialPaymentDto } from '../models/financialPayment.model';
import { FinancialInstallment } from '../models/financialInstallment.model';
import { InstallmentSettlementPreviewDto } from '../models/installment-settlement-preview.model';
import { environment } from '../../environments/environment';
import { ExpenseTypeEnum } from '../models/enums/expenseType.enum';
import { ExpenseDashboardSummaryDto } from '../models/expenseDashboardSummary.model';
import { EmployeeExpenseSummaryDto } from '../models/employeeExpenseSummary.model';
import { SupplierExpenseSummaryDto } from '../models/supplierExpenseSummary.model';
import { CompanyExpenseSummaryDto } from '../models/companyExpenseSummary.model';

@Injectable({
  providedIn: 'root'
})
export class FinancialService {

  private readonly baseUrl = `${environment.base_url}/financials`;

  constructor(private http: HttpClient) {}

  getAll(search: string, status: string): Observable<PaginatedList<any[]>> {
    return this.http.get<PaginatedList<any[]>>(`${this.baseUrl}/list`, {params: {status, search}});// any até eu contruir o dto
  }

  getAllExpenses(search: string, status: string): Observable<PaginatedList<any[]>> {
    return this.http.get<PaginatedList<any[]>>(`${this.baseUrl}/expenses`, {params: {status, search}});
  }

  getById(id: number): Observable<Financial> {
    return this.http.get<Financial>(`${this.baseUrl}/find/${id}`);
  }

  getFinancialSummary(search: string = '', status: string = '', page: number = 1, customerId?: number, dateRange: DateRangeEnum = DateRangeEnum.All): Observable<RestResponse<FinancialSummary[]>> {
    const params: any = { search, status, dateRange, page };
    if (customerId) {
      params.customerId = customerId.toString();
    }
    return this.http.get<RestResponse<FinancialSummary[]>>(`${this.baseUrl}/summary`, { params });
  }

  getFinancialExpenseSummary(search: string = '', status: string = '', page: number = 1, expenseType: ExpenseTypeEnum, id?: number, dateRange: DateRangeEnum = DateRangeEnum.All): Observable<RestResponse<FinancialSummary[]>> {
    const params: any = { search, status, dateRange, page, expenseType };
    if (id) {
      params.id = id.toString();
    }
    return this.http.get<RestResponse<FinancialSummary[]>>(`${this.baseUrl}/expense-summary`, { params });
  }

  create(financial: CreateFinancialDto): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/create`, financial);
  }

  createExpense(expense: CreateExpenseDto): Observable<RestResponse<object>> {
    return this.http.post<RestResponse<object>>(`${this.baseUrl}/expense/create`, expense);
  }

  update(id: number, financial: Financial): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/${id}`, financial);
  }

  delete(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${id}`);
  }

  cancel(id: number): Observable<void> {
    return this.http.patch<void>(`${this.baseUrl}/cancel/${id}`, null);
  }

  settleInstallment(payload: CreateFinancialPaymentDto): Observable<RestResponse<any>> {
    return this.http.post<RestResponse<any>>(`${this.baseUrl}/installments/settle`, payload);
  }

  settlementPreview(financialId: number, installmentId: number, date: string): Observable<RestResponse<InstallmentSettlementPreviewDto>> {
    const body = {
      FinancialId: financialId,
      InstallmentId: installmentId,
      Date: date
    };
    return this.http.post<RestResponse<InstallmentSettlementPreviewDto>>(
      `${this.baseUrl}/installments/settlement-preview`,
      body
    );
  }

  getInstallments(financialId: number): Observable<RestResponse<FinancialInstallment[]>> {
    return this.http.get<RestResponse<FinancialInstallment[]>>(`${this.baseUrl}/${financialId}/installments`);
  }

  getDashboard(search: string, status: string, city: string | null, dateRange: DateRangeEnum): Observable<RestResponse<FinancialDashboard>> {
    let params: any = { status, search, dateRange };
    
    if (city) {
      params.city = city;
    }
    
    return this.http.get<RestResponse<FinancialDashboard>>(`${this.baseUrl}/dashboard-summary`, {params});
  }

  getDashboardExpenses(search: string, status: string, dateRange: DateRangeEnum, expenseType: ExpenseTypeEnum): Observable<RestResponse<ExpenseDashboardSummaryDto>> {
    return this.http.get<RestResponse<ExpenseDashboardSummaryDto>>(`${this.baseUrl}/expenses-dashboard-summary`, {params: {status, search, dateRange, expenseType}});
  }

  getExpenseSummary(search: string, status: string, page: number = 1, id: number, dateRange: DateRangeEnum = DateRangeEnum.All, expenseType: ExpenseTypeEnum): Observable<RestResponse<SupplierExpenseSummaryDto[] | EmployeeExpenseSummaryDto[] | CompanyExpenseSummaryDto[]>> {
    return this.http.get<RestResponse<SupplierExpenseSummaryDto[] | EmployeeExpenseSummaryDto[] | CompanyExpenseSummaryDto[]>>(`${this.baseUrl}/expense-ecs-summary`, {params: {search, status, page, dateRange, expenseType, id}});
  }


  exportA4(id: number): Observable<Blob> {
    return this.http.get<Blob>(
      `${this.baseUrl}/reports/print-a4/${id}`,
      {responseType: 'blob' as 'json'}
    );
  }

  exportHistoryA4(id: number): Observable<Blob> {
    return this.http.get<Blob>(
      `${this.baseUrl}/reports/print-list-a4/${id}`,
      {responseType: 'blob' as 'json'}
    );
  }
}
